import { Component, EventEmitter, inject, Output, signal, ViewChild } from '@angular/core';
import { DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { Params } from '../../../../services/utilis/add-params-service';
import { FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { map, distinctUntilChanged, tap, switchMap } from 'rxjs';
import { MileageService } from '../../../../services/setting/plans/mileage-service';
import { MileagePlans } from '../../../../models/mileage-plans';

@Component({
  selector: 'app-mileage-plan-component',
  imports: [TableComponent],
  templateUrl: './mileage-plan-component.html',
  styleUrl: './mileage-plan-component.scss'
})
export class MileagePlanComponent {

  @ViewChild(TableComponent) table!: TableComponent<MileagePlans>;

  @Output() editMileagePlan = new EventEmitter<MileagePlans>();
  @Output() deleteMileagePlan = new EventEmitter<MileagePlans>();

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'minBasePrice',
    'maxBasePrice',
    'isActive',
    'minAdditionalKilometers',
    'maxAdditionalKilometers',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    minBasePrice: "minBasePrice",
    maxBasePrice: "maxBasePrice",
    isActive: "isActive",
    minAdditionalKilometers: "minAdditionalKilometers",
    maxAdditionalKilometers: "maxAdditionalKilometers",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<MileagePlans>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    { header: 'Name', field: 'name', sortable: true, },
    {
      header: 'Additional Kilometers',
      field: 'additionalKilometers',
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 1000000,
      step: 1000,
    },
    {
      header: 'Base price',
      field: 'basePrice',
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 10000,
      step: 100,
    },
    { header: 'Discount', field: 'discountPercentage', sortable: true, suffixText: "%", iconPosition: "right" },
    {
      header: 'Final price',
      field: 'finalPrice',
      sortable: false,
    },
    { header: 'Saved amount', field: 'savedAmount', sortable: false },
    { header: 'Model count', field: 'modelCount', sortable: false },
    {
      header: 'Activated',
      field: 'isActive',
      isBoolean: true,
      sortable: false,
      trueLabel: "Activated",
      falseLabel: "Deactivated",
      isFilterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
      ],
    },

    {
      field: 'actions',
      header: 'Actions',
      sortable: false,
      actions: [
        {
          icon: 'edit-icon.svg',
          command: (mileagePlan) => this.editMileagePlan.emit(mileagePlan),

        },
        {
          icon: 'trash-icon.svg',
          command: (mileagePlan) => this.deleteMileagePlan.emit(mileagePlan),
        },
      ],
    },
  ];

  mileageList = signal<MileagePlans[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 10;
  searchControl = new FormControl('');

  private mileageService = inject(MileageService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)


  loadData() {
    this.initializeQueryParamsObservable();
  }

  initializeQueryParamsObservable = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      // 🧠 Deep comparison without Lodash
      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.mileageService.getMileagePlansList(params);
      })
    )
      .subscribe((res) => {
        this.mileageList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  };

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  resetAll() {
    this.table?.clearAllFilters();
  }
}
